import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

const DEFAULT_LAUNCH_CRITICAL_PATH = {
  enabled: true,
  label: 'Current Focus',
  helper_text: 'Keep the few highest-priority items in view above the daily queue.',
  items: [],
};

const FOUNDING_40_SERIES_DATES = [
  '2026-05-31',
  '2026-06-07',
  '2026-06-14',
  '2026-06-28',
  '2026-07-06',
];

const createDefaultSiteSettingsForm = () => ({
  zoom_link: '',
  intro_video_url: '',
  founding_40_start_date: '2026-05-31',
  founding_40_time: '4:00 PM ET',
  founding_40_series_dates: [...FOUNDING_40_SERIES_DATES],
  founding_40_public_mode: 'join',
  max_founding_members: 40,
  upcoming_events: [],
  launch_critical_path: { ...DEFAULT_LAUNCH_CRITICAL_PATH },
});

const normalizeLaunchCriticalPath = (value) => {
  if (!value || typeof value !== 'object') {
    return { ...DEFAULT_LAUNCH_CRITICAL_PATH };
  }

  return {
    enabled: value.enabled !== false,
    label: value.label || DEFAULT_LAUNCH_CRITICAL_PATH.label,
    helper_text: value.helper_text || DEFAULT_LAUNCH_CRITICAL_PATH.helper_text,
    items: Array.isArray(value.items) ? value.items : [],
  };
};

const normalizeSiteSettingsPayload = (value = {}) => ({
  ...value,
  founding_40_series_dates: Array.isArray(value.founding_40_series_dates)
    ? value.founding_40_series_dates
    : [...FOUNDING_40_SERIES_DATES],
  upcoming_events: Array.isArray(value.upcoming_events) ? value.upcoming_events : [],
  launch_critical_path: normalizeLaunchCriticalPath(value.launch_critical_path),
});

export function useSiteSettingsAdminState({
  activeTab,
  adminFetch,
  hasToken,
}) {
  const [siteSettingsOpenRequestKey, setSiteSettingsOpenRequestKey] = useState(0);
  const [siteSettings, setSiteSettings] = useState(null);
  const [siteSettingsForm, setSiteSettingsForm] = useState(createDefaultSiteSettingsForm);
  const [siteSettingsSaving, setSiteSettingsSaving] = useState(false);
  const [siteSettingsLoading, setSiteSettingsLoading] = useState(true);

  const fetchSiteSettings = useCallback(async () => {
    if (!hasToken) return;

    try {
      const response = await adminFetch('/admin/site-settings');
      if (response.ok) {
        const data = await response.json();
        setSiteSettings(normalizeSiteSettingsPayload(data ?? {}));
      }
    } catch (_) {
      // Keep siteSettings as null on error or 404
    }
  }, [adminFetch, hasToken]);

  const fetchSiteSettingsForm = useCallback(async () => {
    setSiteSettingsLoading(true);
    try {
      const response = await adminFetch('/admin/site-settings');
      if (response.ok) {
        const data = normalizeSiteSettingsPayload(await response.json());
        setSiteSettingsForm((prev) => ({ ...prev, ...data }));
      } else {
        console.error('Failed to fetch settings:', await response.text());
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setSiteSettingsLoading(false);
    }
  }, [adminFetch]);

  const handleSaveSiteSettings = useCallback(async () => {
    setSiteSettingsSaving(true);
    try {
      const response = await adminFetch('/admin/site-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(siteSettingsForm),
        timeoutMs: 60000,
      });

      if (response.ok) {
        const data = normalizeSiteSettingsPayload(await response.json());
        setSiteSettings(data);
        setSiteSettingsForm(data);
        toast.success('Settings saved successfully!');
      } else {
        const raw = await response.text().catch(() => '');
        let detail = '';
        try {
          const parsed = raw ? JSON.parse(raw) : null;
          detail = typeof parsed?.detail === 'string' ? parsed.detail : '';
        } catch (_) {
          detail = '';
        }
        console.error('Failed to save settings:', raw || response.statusText);
        toast.error(detail || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error?.message === 'request timeout' ? 'Request timed out. Try again.' : 'Failed to save settings');
    } finally {
      setSiteSettingsSaving(false);
    }
  }, [adminFetch, siteSettingsForm]);

  const handleTabRequest = useCallback((nextTab) => {
    if (nextTab === 'sitesettings' && activeTab === 'sitesettings') {
      setSiteSettingsOpenRequestKey(Date.now());
    }
  }, [activeTab]);

  useEffect(() => {
    fetchSiteSettings();
  }, [fetchSiteSettings]);

  useEffect(() => {
    if (activeTab !== 'sitesettings') return;

    setSiteSettingsOpenRequestKey(Date.now());
    fetchSiteSettingsForm();
  }, [activeTab, fetchSiteSettingsForm]);

  const siteSettingsTabProps = useMemo(() => ({
    settings: siteSettingsForm,
    setSettings: setSiteSettingsForm,
    saving: siteSettingsSaving,
    loading: siteSettingsLoading,
    openRequestKey: siteSettingsOpenRequestKey,
    handleSave: handleSaveSiteSettings,
    adminFetch,
  }), [
    adminFetch,
    handleSaveSiteSettings,
    siteSettingsForm,
    siteSettingsLoading,
    siteSettingsOpenRequestKey,
    siteSettingsSaving,
  ]);

  return {
    siteSettings,
    handleTabRequest,
    siteSettingsTabProps,
  };
}
